import { dealsQuery } from "@/lib/route-queries";
import { createFileRoute } from "@tanstack/react-router";
import ogHomeImg from "@/assets/og/og-home.jpg";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ProductCard } from "@/components/ProductCard";
import { StorefrontPageShell } from "@/components/storefront/StorefrontPageShell";
import { listProducts } from "@/lib/catalog.functions";
import { SITE_URL, breadcrumbScript, ogImageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { useT } from "@/i18n/LanguageProvider";
import { Button } from "@/components/ui/button";
import { isDealLive } from "@/lib/deal-clock";
import { isEndingSoon, sortDealsByEnd } from "@/lib/flash-deals";
import { oneOf, searchShape } from "@/lib/routing/search-parsers";

const dealsSearchSchema = searchShape({ ending: oneOf(["all", "soon"] as const, "all") });

export const Route = createFileRoute("/deals")({
  validateSearch: dealsSearchSchema,
  loader: ({ context }) => context.queryClient.ensureQueryData(dealsQuery()),
  head: ({ loaderData }) => {
    const url = `${SITE_URL}/deals`;
    const list = Array.isArray(loaderData) ? (loaderData as any[]) : [];
    const deals = list
      .filter((p) => isDealLive(p))
      .slice(0, 20);
    const description =
      "Discounted brake pads, filters, oils, tyres and more from verified vendors across Bangladesh.";
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      url,
      name: "Best deals on auto parts",
      description,
      inLanguage: ["en", "bn"],
      isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: `${SITE_URL}/` },
    };
    const itemListLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      numberOfItems: deals.length,
      itemListElement: deals
        .filter((p) => p.slug)
        .map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${SITE_URL}/products/${p.slug}`,
          name: p.name_en || p.name || p.slug,
        })),
    };
    return {
      meta: [
        { title: "ফ্ল্যাশ ডিল — Best deals on auto parts | Jantro Bazar" },
        { name: "description", content: description },
        { property: "og:title", content: "Best deals on auto parts" },
        {
          property: "og:description",
          content: "Discounts on genuine parts from verified vendors.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        ...ogImageMeta(`${SITE_URL}${ogHomeImg}`, "Best deals on auto parts | Jantro Bazar"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbScript("Deals", url),
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(itemListLd) },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-muted-foreground">{error.message}</div>
  ),
  notFoundComponent: () => (
    <div className="p-8 text-center text-sm text-muted-foreground">
      No deals right now. / এই মুহূর্তে কোনো অফার নেই।
    </div>
  ),
  component: DealsPage,
});

function DealsPage() {
  const { lang } = useT();
  const { ending } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => { setNow(Date.now()); const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const fn = useServerFn(listProducts);
  const { data, isLoading, isError, error, refetch } = useSuspenseQuery({
    ...dealsQuery(),
    queryFn: () => fn({ data: { sort: "popular", in_stock: true, limit: 60 } as any }),
  });
  const deals = sortDealsByEnd(((data as any[]) ?? []).filter(
    (p) => isDealLive(p, now ?? 0) && (ending !== "soon" || isEndingSoon(p, now ?? 0)),
  ));
  return (
    <StorefrontPageShell
      testId="deals-page"
      title="Deals"
      titleBn="অফার"
      description="Discounted, in-stock parts from verified vendors."
      descriptionBn="যাচাইকৃত বিক্রেতাদের কাছ থেকে ডিসকাউন্টে স্টকে থাকা পার্টস।"
      loading={isLoading || now === null}
      error={isError ? error : undefined}
      onRetry={() => void refetch()}
      rowCount={deals.length}
      emptyTitle={ending === "soon" ? "No deals ending within 48 hours" : "No active discounts right now"}
      emptyTitleBn={ending === "soon" ? "৪৮ ঘণ্টার মধ্যে শেষ হবে এমন ডিল নেই" : "এই মুহূর্তে কোনো সক্রিয় ডিসকাউন্ট নেই"}
      emptyHint="Check back soon — vendors add new offers every week."
      emptyHintBn="শীঘ্রই আবার দেখুন — বিক্রেতারা প্রতি সপ্তাহে নতুন অফার যোগ করেন।"
      filters={<div className="flex flex-wrap gap-2" role="group" aria-label={lang === "bn" ? "ডিল বাছাই" : "Filter deals"}>
        {(["all", "soon"] as const).map((value) => <Button key={value} variant={ending === value ? "default" : "outline"} className="min-h-11" aria-pressed={ending === value} onClick={() => navigate({ search: { ending: value }, replace: true })}>
          {value === "all" ? (lang === "bn" ? "সব ডিল" : "All deals") : (lang === "bn" ? "শিগগির শেষ · ৪৮ ঘণ্টা" : "Ending soon · 48 hours")}
        </Button>)}
      </div>}
    >
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {deals.map((p: any) => (
            <ProductCard key={p.id ?? p.slug} p={p} deal />
        ))}
      </div>
    </StorefrontPageShell>
  );
}
